import type { Purchase } from '@features/auth/model/types.js';
import {
  applyCookieConsent,
  setAnalyticCookiesAllowed,
} from '@features/cookieConsent/model/actions.js';
import type { DrawingStyle } from '@features/drawing/model/reducers/drawingSettingsReducer.js';
import {
  saveHomeLocation,
  setSelectingHomeLocation,
} from '@features/homeLocation/model/actions.js';
import {
  locationSetHeadingSource,
  locationSetShowBearingLine,
  setLocation,
  toggleLocate,
} from '@features/location/model/actions.js';
import type { LayerSettings } from '@features/map/model/actions.js';
import type { LayerSetup } from '@features/map/model/layerSetup.js';
import type { MapPreset } from '@features/map/model/mapPreset.js';
import { createAction } from '@reduxjs/toolkit';
import type { CustomLayerDef } from '@shared/mapDefinitions.js';
import type { FeatureId, OsmFeatureId } from '@shared/types/featureId.js';
import type { Feature, FeatureCollection } from 'geojson';
import z from 'zod';
import type { ActiveModal } from './activeModal.js';

export const ToolSchema = z.enum([
  'changesets',
  'draw-lines',
  'draw-points',
  'draw-polygons',
  'gps-recorder',
  'import-file',
  'map-details',
  // Opens nothing — the objects toolbar follows the category filter rather
  // than a tool. Kept so a `#tools=objects` in an older shared link still
  // parses instead of being refused.
  'objects',
  'panorama',
  'route-planner',
  'toposcope',
  'tracking',
]);

export type Tool = z.infer<typeof ToolSchema>;

/**
 * Opens a tool. A map-click tool replaces the open one — at most one of them
 * owns map clicks — and drops the selection unless it owns the selected
 * feature. A tool that only brings a toolbar joins the ones already open and
 * leaves the selection alone.
 */
export const openTool = createAction<Tool>('OPEN_TOOL');

/**
 * Closes one named tool. There is no "close whatever is open": a reducer that
 * clears its slice when its tool goes has to tell that from another tool being
 * opened beside it.
 */
export const closeTool = createAction<Tool>('CLOSE_TOOL');

export const setActiveModal = createAction<ActiveModal | null>(
  'SET_ACTIVE_MODAL',
);

export { setLocation };

export const clearMapFeatures = createAction('CLEAR_MAP_FEATURES');

/** Drops the persisted local settings and reloads the app from defaults. */
export const resetApp = createAction('RESET_APP');

export {
  locationSetHeadingSource,
  locationSetShowBearingLine,
  saveHomeLocation,
  setSelectingHomeLocation,
  toggleLocate,
};

type Settings = {
  layersSettings?: Record<string, LayerSettings>;
  layerSetups?: Record<string, LayerSetup>;
  overlayPaneOpacity?: number;
  customLayers?: CustomLayerDef[];
  presets?: MapPreset[];
  maxZoom?: number;
  drawing?: Partial<DrawingStyle>;
};

export const saveSettings = createAction<{
  settings?: Pick<Settings, 'maxZoom'>;
  user?: {
    name?: string;
    email?: string | null;
    description?: string | null;
    sendGalleryEmails?: boolean;
    picture?: string | null;
  };
  keepOpen?: boolean;
}>('SAVE_SETTINGS');

export const applySettings = createAction<
  Settings & { drawingApplyAll?: boolean }
>('APPLY_SETTINGS');

export const setErrorTicketId = createAction<string | undefined>(
  'SET_ERROR_TICKET_ID',
);

export const setEmbedFeatures = createAction<string[]>('SET_EMBED_FEATURES');

export const purchase = createAction<Purchase>('PURCHASE');

/**
 * Deletes the selected feature, or `target` where given — then leaving the
 * selection, renumbered where the deletion shifts it.
 */
export const deleteFeature = createAction<Selection | undefined>(
  'DELETE_FEATURE',
);

/** A point, line or polygon of the map's own features. */
export interface FeatureSelection {
  type: 'feature';
  /** Index into `trackViewer.trackGeojson.features`. */
  id: number;
  /** A polygon's hole, by ring index, where the hole rather than the shape is meant. */
  part?: number;
}

/** One vertex of a feature; see `VertexAddress`. */
export interface VertexSelection {
  type: 'vertex';
  /** Index into `trackViewer.trackGeojson.features`. */
  id: number;
  part: number;
  vertex: number;
}

export interface ObjectsSelection {
  type: 'objects';
  id: OsmFeatureId;
}

export interface TrackingSelection {
  type: 'tracking';
  /** The device's token. */
  id: string;
}

export interface RoutePointSelection {
  type: 'route-point';
  id: number;
}

export interface RouteSegmentSelection {
  type: 'route-leg';
  id: number;
}

export interface SearchSelection {
  type: 'search';
  /** Which of the shown results this is — the map can hold several at once. */
  id: FeatureId;
}

export interface ChangesetElementSelection {
  type: 'changeset-element';
  /** `node/123`, an element of the inspected changeset. */
  id: string;
}

export type Selection =
  | ChangesetElementSelection
  | FeatureSelection
  | VertexSelection
  | ObjectsSelection
  | TrackingSelection
  | RoutePointSelection
  | RouteSegmentSelection
  | SearchSelection;

export const selectFeature = createAction<Selection | null>('SELECT_FEATURE');

/**
 * The same feature, re-addressed after a deletion shifted it. Unlike
 * `selectFeature`, it leaves modes and the details toast as they are.
 */
export const selectionRenumbered = createAction<Selection | null>(
  'SELECTION_RENUMBERED',
);

/**
 * What the conversion dialog answered about the features' own data; see
 * `useConvertPrompt`. Absent, a conversion carries what it always has.
 */
export type ConvertCarry = {
  /** Property keys to carry onto the drawn feature; the rest are dropped. */
  keys: string[];
  /**
   * The label to give every converted feature, `{p:key}` reaching the
   * properties above. Absent, each keeps the label it would have got.
   */
  label?: string;
  /** Bake the label from the source's own properties instead of carrying it. */
  resolveLabel: boolean;
};

export const convertToDrawing = createAction<
  // objects: `id` omitted → bulk-convert every visible object as a point
  | { type: 'objects'; id?: OsmFeatureId; carry?: ConvertCarry }
  // objects-geometry: `geojson` is the element already fetched by the dialog
  // that asked, which then also settled `tolerance`; without it both are done
  // by the processor.
  | {
      type: 'objects-geometry';
      id: OsmFeatureId;
      carry?: ConvertCarry;
      geojson?: Feature;
      tolerance?: number;
    }
  | { type: 'planned-route'; tolerance: number }
  | { type: 'search-result'; tolerance: number; carry?: ConvertCarry }
  | { type: 'changesets' }
  // geojson: features built by the caller, copied as they are
  | { type: 'geojson'; geojson: FeatureCollection }
>('CONVERT_TO_DRAWING');

export type ExternalTarget =
  | 'apple'
  | 'copy'
  | 'f4map'
  | 'google'
  | 'hiking.sk'
  | 'image'
  | 'josm'
  | 'mapillary'
  | 'mapy.com'
  | 'oma.sk'
  | 'osm.org'
  | 'osm.org/id'
  | 'panoramax'
  | 'peakfinder'
  | 'streetview'
  | 'url'
  | 'waze'
  | 'window'
  | 'windy'
  | 'zbgis';

export const openInExternalApp = createAction<{
  where: ExternalTarget;
  lat?: number;
  lon?: number;
  zoom?: number;
  mapType?: string;
  includePoint?: boolean;
  pointTitle?: string;
  /**
   * What the point is, as OSM tags — JOSM adds the node carrying them. Given
   * (even empty) it decides the tags outright; without it the title, where
   * there is one, goes as the `name`.
   */
  pointTags?: Record<string, string>;
  pointDescription?: string;
  /** The page this is about: opened in a window, or shared as a link. */
  url?: string;
  /**
   * The picture itself, for `image` — the one target that shares a file rather than an address, so
   * it needs the bytes and not a page about them. Separate from `url` because the two differ:
   * a Wikimedia photo's `url` is its Commons file page.
   */
  imageUrl?: string;
}>('OPEN_IN_EXTERNAL');

export { applyCookieConsent, setAnalyticCookiesAllowed };

export const hideInfoBar = createAction<{
  key: string;
  ts: number;
}>('HIDE_INFO_BAR');

/** Records that an info bar was displayed, so that the next one gets its turn. */
export const infoBarShown = createAction<{
  key: string;
  ts: number;
}>('INFO_BAR_SHOWN');

export const init = createAction('INIT');
